import React from "react";
import ReactMarkdown from "react-markdown";
import { Bot, User, Loader2 } from "lucide-react";

/**
 * ReactMarkdown component overrides — filters out the internal `node` prop
 * before spreading to DOM elements to avoid React warnings about unknown
 * DOM attributes. Defined at module scope so the reference is stable.
 */
const MARKDOWN_COMPONENTS = {
  p: ({ node: _n, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
  ul: ({ node: _n, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
  ol: ({ node: _n, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
  li: ({ node: _n, ...props }) => <li {...props} />,
  strong: ({ node: _n, ...props }) => (
    <strong className="font-semibold text-[#0e3a3f]" {...props} />
  ),
  h1: ({ node: _n, ...props }) => (
    <h4 className="font-display text-base font-semibold mt-3 mb-1.5 text-[#0e3a3f]" {...props} />
  ),
  h2: ({ node: _n, ...props }) => (
    <h4 className="font-display text-base font-semibold mt-3 mb-1.5 text-[#0e3a3f]" {...props} />
  ),
  h3: ({ node: _n, ...props }) => (
    <h5 className="font-semibold text-sm mt-2 mb-1 text-[#2c7a80] uppercase tracking-wide" {...props} />
  ),
  h4: ({ node: _n, ...props }) => (
    <h5 className="font-semibold text-sm mt-2 mb-1 text-[#2c7a80]" {...props} />
  ),
  a: ({ node: _n, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#2c7a80] underline hover:text-[#0e3a3f]"
    />
  ),
  hr: () => <hr className="my-3 border-[#e6ded0]" />,
};

function Avatar({ role }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
        isUser ? "bg-[#e8a04d]" : "bg-gradient-to-br from-[#2c7a80] to-[#4ba9b0]"
      }`}
    >
      {isUser ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
    </div>
  );
}

function AssistantBubble({ content, streaming }) {
  const hasContent = content && content.length > 0;
  return (
    <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-white border border-[#e6ded0] text-[#0e3a3f] rounded-tl-sm">
      <div className="text-sm leading-relaxed prose-chat">
        <ReactMarkdown components={MARKDOWN_COMPONENTS}>{content || ""}</ReactMarkdown>
        {streaming && !hasContent && <Loader2 className="w-4 h-4 inline-block animate-spin" />}
        {streaming && hasContent && (
          <span className="inline-block w-1.5 h-4 bg-[#2c7a80] animate-pulse ml-1 align-middle" />
        )}
      </div>
    </div>
  );
}

function UserBubble({ content }) {
  return (
    <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-[#2c7a80] text-white rounded-tr-sm">
      <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>
    </div>
  );
}

export default function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <Avatar role={message.role} />
      {isUser ? (
        <UserBubble content={message.content} />
      ) : (
        <AssistantBubble content={message.content} streaming={message.streaming} />
      )}
    </div>
  );
}
